import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col
} from 'reactstrap';
// ⬇️ usar la versión externa HMAC
import ExtAddressAutocomplete from 'components/extAddressAutocomplete';

const BusinessAddressModal = ({ isOpen, toggle, onSave, addressToEdit }) => {
  const [formData, setFormData] = useState({
    addressType: 'PHYSICAL',
    streetAddress: '',
    streetAddress2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    phoneNumber: ''
  });
  const [errors, setErrors] = useState({});

  const availableTypes = ['PHYSICAL', 'MAILING', 'BILLING'];

  useEffect(() => {
    if (addressToEdit) {
      setFormData({
        addressType: addressToEdit.addressType || 'PHYSICAL',
        streetAddress: addressToEdit.streetAddress || '',
        streetAddress2: addressToEdit.streetAddress2 || '',
        city: addressToEdit.city || '',
        state: addressToEdit.state || '',
        zipCode: addressToEdit.zipCode || '',
        country: addressToEdit.country || 'USA',
        phoneNumber: addressToEdit.phoneNumber || ''
      });
    } else {
      setFormData({
        addressType: 'PHYSICAL',
        streetAddress: '',
        streetAddress2: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA',
        phoneNumber: ''
      });
    }
    setErrors({});
  }, [addressToEdit, isOpen]);

  const handleInputChange = (field, value) => {
    let processedValue = value;
    if (field === 'zipCode') processedValue = value.slice(0, 5);

    setFormData(prev => ({ ...prev, [field]: processedValue }));

    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleAddressSelect = (item) => {
    const label = item?.label || '';
    const details = item?.details || {};

    setFormData(prev => ({
      ...prev,
      streetAddress: label,
      city: details.city || '',
      state: details.state || '',
      zipCode: (details.zipCode || '').slice(0, 5),
      country: details.country || 'USA'
    }));

    setErrors(prev => {
      const next = { ...prev };
      delete next.streetAddress;
      delete next.city;
      delete next.state;
      delete next.zipCode;
      return next;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.addressType) newErrors.addressType = 'Address type is required';
    if (!formData.streetAddress || formData.streetAddress.trim() === '') newErrors.streetAddress = 'Street address is required';
    if (!formData.city || formData.city.trim() === '') newErrors.city = 'City is required';
    if (!formData.state || formData.state.trim() === '') newErrors.state = 'State is required';
    if (!formData.zipCode || formData.zipCode.trim() === '') newErrors.zipCode = 'Zip code is required';
    else if (formData.zipCode.length > 5) newErrors.zipCode = 'Zip code cannot exceed 5 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) onSave(formData);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <h5 className="mb-0">
          <i className="ni ni-pin-3 mr-2" />
          {addressToEdit ? 'Edit Business Address' : 'Add Business Address'}
        </h5>
      </ModalHeader>
      <ModalBody>
        <Form>
          <Row>
            <Col md="6">
              <FormGroup className="mb-3">
                <Label htmlFor="addressType" className="form-control-label">Address Type</Label>
                <Input
                  id="addressType"
                  type="select"
                  value={formData.addressType}
                  onChange={(e) => handleInputChange('addressType', e.target.value)}
                  className={errors.addressType ? 'is-invalid' : ''}
                  bsSize="sm"
                >
                  <option value="">Select Address Type</option>
                  {availableTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Input>
                {errors.addressType && (
                  <div className="invalid-feedback d-block">{errors.addressType}</div>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup className="mb-3">
                <Label htmlFor="phoneNumber" className="form-control-label">Phone Number (Optional)</Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="Enter phone number"
                  maxLength={20}
                  bsSize="sm"
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="mb-3">
            <Label htmlFor="streetAddress" className="form-control-label">Street Address</Label>
            <ExtAddressAutocomplete
              value={formData.streetAddress}
              onChange={(text) => handleInputChange('streetAddress', text)}
              onSelect={handleAddressSelect}
              placeholder="Start typing address..."
              className={errors.streetAddress ? 'is-invalid' : ''}
            />
            {errors.streetAddress && (
              <div className="invalid-feedback d-block">{errors.streetAddress}</div>
            )}
          </FormGroup>

          <FormGroup className="mb-3">
            <Label htmlFor="streetAddress2" className="form-control-label">Street Address 2 (Optional)</Label>
            <Input
              id="streetAddress2"
              type="text"
              value={formData.streetAddress2}
              onChange={(e) => handleInputChange('streetAddress2', e.target.value)}
              placeholder="Apartment, suite, unit, etc."
              bsSize="sm"
            />
          </FormGroup>

          <Row>
            <Col md="4">
              <FormGroup className="mb-3">
                <Label htmlFor="city" className="form-control-label">City</Label>
                <Input
                  id="city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className={errors.city ? 'is-invalid' : ''}
                  bsSize="sm"
                />
                {errors.city && <div className="invalid-feedback d-block">{errors.city}</div>}
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup className="mb-3">
                <Label htmlFor="state" className="form-control-label">State</Label>
                <Input
                  id="state"
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className={errors.state ? 'is-invalid' : ''}
                  bsSize="sm"
                />
                {errors.state && <div className="invalid-feedback d-block">{errors.state}</div>}
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup className="mb-3">
                <Label htmlFor="zipCode" className="form-control-label">Zip Code</Label>
                <Input
                  id="zipCode"
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  className={errors.zipCode ? 'is-invalid' : ''}
                  maxLength={5}
                  bsSize="sm"
                />
                {errors.zipCode && <div className="invalid-feedback d-block">{errors.zipCode}</div>}
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} size="sm">
          <i className="ni ni-fat-remove mr-2" />
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} size="sm">
          <i className="ni ni-check-bold mr-2" />
          {addressToEdit ? 'Update' : 'Add'} Address
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BusinessAddressModal;